import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Loader, ProgressBar } from "@/components/shared";
import { Input } from "@/components/ui/input";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function Performance() {
  const qc = useQueryClient();
  const { data: perf, isLoading } = useQuery({ queryKey: ["performance"], queryFn: async () => (await api.get("/performance")).data });
  const { data: stories } = useQuery({ queryKey: ["stories"], queryFn: async () => (await api.get("/stories")).data });
  if (isLoading || !perf) return <Loader />;

  const toggle = async (item) => {
    const checklist = { ...perf.checklist, [item]: !perf.checklist[item] };
    await api.put("/performance", { checklist });
    qc.invalidateQueries({ queryKey: ["performance"] });
    qc.invalidateQueries({ queryKey: ["dash-student"] });
    if (checklist[item]) toast.success(`✅ ${item} done!`);
  };

  const setStory = async (id) => {
    await api.put("/performance", { selected_story_id: id });
    qc.invalidateQueries({ queryKey: ["performance"] });
    toast.success("Story selected for the Grand Finale!");
  };

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="rounded-3xl gradient-blue p-6 text-white">
        <div className="flex items-center gap-2 text-csh-orange font-bold uppercase"><Icons.Trophy size={20} /> Grand Finale</div>
        <h1 className="font-head text-4xl">Your Performance Journey</h1>
        <div className="mt-4 flex flex-wrap gap-3 text-sm font-bold">
          <span className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5"><Icons.CalendarDays size={15} /> {perf.event_date || "Date TBA"}</span>
          <span className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5"><Icons.MapPin size={15} /> {perf.venue || "Venue TBA"}</span>
        </div>
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5 text-center">
        <div className="font-head text-6xl text-csh-orange">{perf.readiness_pct}%</div>
        <p className="font-bold uppercase text-muted-foreground">Performance Ready</p>
        <ProgressBar pct={perf.readiness_pct} className="mt-3" />
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
        <h3 className="font-head text-2xl text-csh-blue mb-3">Rehearsal Checklist</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          {perf.checklist_items.map((item) => {
            const done = perf.checklist[item];
            return (
              <button key={item} onClick={() => toggle(item)} data-testid={`checklist-${item.replace(/ /g, "-")}`}
                className={`flex items-center gap-3 rounded-2xl px-4 py-3 text-left font-semibold transition-colors ${done ? "bg-green-50 border-2 border-green-300" : "bg-black/5 border-2 border-transparent"}`}>
                {done ? <Icons.CheckCircle2 className="text-green-500 shrink-0" /> : <Icons.Circle className="text-csh-grey/40 shrink-0" />}
                <span className={done ? "text-green-700" : "text-csh-grey"}>{item}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
        <h3 className="font-head text-2xl text-csh-blue mb-1">Select Your Signature Story</h3>
        <p className="text-sm text-muted-foreground mb-3">Choose the story you'll perform at the finale.</p>
        {(!stories || stories.length === 0) ? (
          <p className="text-sm text-muted-foreground">Save stories in My Stories first!</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {stories.map((s) => (
              <button key={s.id} onClick={() => setStory(s.id)} data-testid={`select-story-${s.id}`}
                className={`flex items-center gap-2 rounded-2xl px-4 py-3 text-left font-bold ${perf.selected_story_id === s.id ? "gradient-purple text-white" : "bg-black/5 text-csh-blue"}`}>
                {perf.selected_story_id === s.id ? <Icons.CheckCircle2 size={18} /> : <Icons.BookOpen size={18} />} {s.title}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
